/**
 * PDF viewer modal with editable document title and Postgres-backed title save.
 * @module docViewerModalwithTitle
 */

import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Modal, TextInput } from "risa-oasis-ui_v2";
import { getFileDownloadUrl } from "../../../api/firebase/firestoreService";
import { DocumentViewerTitleMap } from "../../../constants/statusToDocMap/statusToDocMap";
import {
  CANCEL_BUTTON_TEXT,
  DOCUMENT_TITLE_LABEL,
  SAVE_BUTTON_TEXT,
  SAVING_TEXT_FOR_BUTTON,
} from "../../../constants/stringLiterals";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { ModalId } from "../../../enums/modalId";
import { RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { getViewDocumentEventNameWithFallback } from "../../../utils/viewDocumnetEventTypeName";
import { apiWorkerManager } from "../../../workerManagers/workerManager";
import PdfRender from "../../pdfRender/pdfRender";
import { useUpdateDocumentTitle } from "./hooks/useUpdateDocumentTitle";

/** Props for the titled document viewer modal. */
interface DocViewerModalwithTitleProps {
  title?: string;
  fileUrl?: string;
  onClose: () => void;
}

/** Redux modal metadata shape carrying document fields for this viewer. */
interface MetaDataForDocument {
  docData?: {
    doc_title?: string;
    doc_type?: string;
    uploaded_text?: string;
    text_to_display?: string;
    doc_path?: string;
    isUploaded?: boolean;
    doc_id?: string;
    j_code?: string;
    id?: string;
  };
}

/**
 * Loads a signed PDF URL from `doc_path`, allows editing title, and saves via {@link useUpdateDocumentTitle}.
 *
 * @param props - Close handler and optional overrides from the parent.
 */
const DocViewerModalwithTitle = (props: DocViewerModalwithTitleProps) => {
  const {
    generalOrderDetails,
    authOnFileDetails,
    fetchAuthOnFileDetails,
    filteredSiblingOrders,
  } = useDataAccumulatorForSingleOrder();
  const [pdfPath, setPdfPath] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const metaData = useSelector((state: RootState) => state.modal.metaData) as
    | MetaDataForDocument
    | undefined;
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgID = getOrgIdForFetchExternalWorklist();
  const docTitle = useRef<string>(metaData?.docData?.doc_title ?? "");
  const { updateDocumentTitle, updateDocumentTitleState } =
    useUpdateDocumentTitle(
      generalOrderDetails?.order ?? null,
      authOnFileDetails?.auth_on_file ?? null,
      filteredSiblingOrders ?? { siblings: [] },
      async (orderId: string, orgId: string) => {
        await fetchAuthOnFileDetails(orderId, orgId);
      },
    );

  const title = DocumentViewerTitleMap[metaData?.docData?.doc_type ?? ""];

  const isEventLogged = useRef(false);

  /**
   * Resolves `metaData.docData.doc_path` to a download URL and logs a view event once.
   */
  const fetchPdfUrl = async () => {
    if (!metaData?.docData?.doc_path) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const downloadUrl = await getFileDownloadUrl(metaData?.docData?.doc_path);
      if (downloadUrl) {
        setPdfPath(downloadUrl);
        !isEventLogged.current &&
          apiWorkerManager.enqueueAndProcess({
            patient_id:
              generalOrderDetails?.order?.demographics?.patient_id ??
              generalOrderDetails?.order?.demographics?.patient_mrn ??
              "",
            user_id: user?.id ?? "",
            user_email: user?.email ?? "",
            order_id: generalOrderDetails?.order?.order_id ?? "",
            org_id: orgID ?? "",
            event_name: getViewDocumentEventNameWithFallback(
              metaData?.docData?.doc_type ?? "",
            ),
            additional_data: {
              screen_name: ScreenNamesForBigQuery.DOCUMENTS_SCREEN,
              doc_type: metaData?.docData?.doc_type ?? "",
              doc_title: metaData?.docData?.doc_title ?? "",
              doc_path: metaData?.docData?.doc_path ?? "",
            },
          });
        isEventLogged.current = true;
      }
    } catch (error) {
      console.error("Error fetching PDF URL:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Downloads the currently loaded PDF blob using the document title as the filename.
   */
  const handleDownload = async () => {
    if (!pdfPath) return;

    try {
      const response = await fetch(pdfPath);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = metaData?.docData?.doc_title || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading PDF:", error);
    }
  };

  useEffect(() => {
    fetchPdfUrl();
  }, [metaData]);

  return (
    <Modal
      dialogId={ModalId.DOC_VIEWER_MODAL_WITH_TITLE}
      onSave={() => {
        updateDocumentTitle(
          metaData?.docData?.doc_id ?? "",
          docTitle.current,
          props.onClose,
        );
      }}
      title={title}
      saveButtonText={
        updateDocumentTitleState.isLoading
          ? SAVING_TEXT_FOR_BUTTON
          : SAVE_BUTTON_TEXT
      }
      disableSave={updateDocumentTitleState.isLoading}
      cancelText={CANCEL_BUTTON_TEXT}
      heightPercentage={90}
      onCancel={() => {
        props.onClose();
      }}
      onClose={() => {
        props.onClose();
      }}
    >
      <div className="doc-viewer-modal--container flex h-full flex-col gap-3 overflow-y-hidden">
        <div className="doc-viewer-modal--title">
          <TextInput
            id="title"
            label={DOCUMENT_TITLE_LABEL}
            onChange={(e) => {
              docTitle.current = e.value;
            }}
            defaultValue={docTitle.current}
          />
        </div>
        {!isLoading && pdfPath && (
          <div
            className="text mr-1 flex cursor-pointer flex-row items-center justify-end text-sm font-bold text-tertiaryBlue-4 hover:text-tertiaryBlue-5"
            onClick={handleDownload}
          >
            Download
          </div>
        )}
        <div className="h-full overflow-y-scroll">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <span className="text-sm text-gray-500">Loading document...</span>
            </div>
          ) : (
            <PdfRender file={pdfPath} />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default DocViewerModalwithTitle;
